import { SlotComponentProps } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { PaperTypeMap } from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import { generateTestId } from '@vs/functions/generate-test-id';
import { useAccountInfo } from '@vs/hooks/hook-account-info';
import { useSignOut } from '@vs/oidc-client';
import { PortalButton } from '@vs/ui/portal/button';
import { Env } from '@vs/utils/envConfig';
import { FocusTrap } from 'focus-trap-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import AccountInfoCard from './account-info-card';
import EntityInfoCard from './entity-info-card';
import EntitySignupEntry from './entity-signup-entry';
import styles from './portal-avatar-menu.module.css';
import UserAvatar from './user-avatar';

const testIdName = 'avatar-menu';

export type PortalAvatarMenuProps = {
  /** env config of app */
  env: Env;
  /** Should show user's entity information  */
  showEntityInfo?: boolean;
  /** Should show user's entity signup app entry */
  showEntitySignupEntry?: boolean;
};

type SlotPaper = SlotComponentProps<
  OverridableComponent<PaperTypeMap<object, 'div'>>,
  object,
  object
>;

/**
 * Vs portal avatar, account info and settings menu
 *
 * @export
 * @param {PortalAvatarMenuProps} props
 * @returns {ReactElement}
 */
export function PortalAvatarMenu(props: PortalAvatarMenuProps) {
  const { showEntityInfo = false, showEntitySignupEntry = false, env } = props;
  const { data: accountInfo } = useAccountInfo();
  const accountId = accountInfo?.id;
  const signout = useSignOut();
  const { t } = useTranslation('shared');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Add keyboard event handler for Escape key
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && Boolean(anchorEl)) {
      handleClose();
    }
  };

  return (
    <div
      {...generateTestId('block', [testIdName])}
      className={styles['avatar-container']}
      onKeyDown={handleKeyDown}
    >
      <UserAvatar env={env} onClick={handleClick} />
      <Popover
        disableAutoFocus
        className="avatar-menu-popover"
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            ...generateTestId('popover', [testIdName]),
          } as SlotPaper,
        }}
      >
        <FocusTrap
          active={isOpen}
          focusTrapOptions={{
            allowOutsideClick: true,
            escapeDeactivates: false,
            returnFocusOnDeactivate: true,
            initialFocus: false,
          }}
        >
          <div className="w-[320px] rounded-xl">
            <AccountInfoCard env={env} />
            {showEntityInfo && !!accountId && (
              <EntityInfoCard accountId={accountId} />
            )}
            <div onClick={handleClose}>
              {showEntitySignupEntry && <EntitySignupEntry />}
              <div className="flex justify-end py-1 px-6">
                <PortalButton
                  {...generateTestId('btn-like', [testIdName, 'log-out'])}
                  onClick={() => signout()}
                  label={t('avatar_menu.sign_out')}
                  icon="logout"
                />
              </div>
            </div>
          </div>
        </FocusTrap>
      </Popover>
    </div>
  );
}

export default PortalAvatarMenu;
